/**
 * Per-status reference data, surfaced in the API so no response contains an
 * unexplained enum value.
 *
 * `case` and `description` are taken verbatim from the six-case proposer taxonomy
 * documented on ValidatorStatusInSlot in @aztec-labs/stdlib/validators — quoting upstream
 * keeps us honest and makes drift visible when the SDK is bumped. `unknown` is our own
 * addition and carries no case number.
 *
 * `note` is ours: an operator-facing clarification added only where the upstream name
 * invites the wrong conclusion.
 */
export const SLOT_STATUS_INFO = {
    "checkpoint-mined": {
        case: 6,
        description: "Checkpoint published on L1.",
        countsAsMissed: false,
        note: "The only status with definitive L1 confirmation, and the only successful outcome.",
    },
    "checkpoint-valid": {
        case: 5,
        description: "Checkpoint proposal re-executed locally as valid.",
        countsAsMissed: true,
        note: "Reads as success but is a miss: the proposal was validated on p2p and never " +
            "published, so the chain skipped the slot. The proposer was live and attested, " +
            "which puts the failure on the L1 submission path rather than the validator.",
    },
    "checkpoint-invalid": {
        case: 4,
        description: "Checkpoint proposal re-executed and rejected as invalid.",
        countsAsMissed: true,
    },
    "checkpoint-unvalidated": {
        case: 3,
        description: "Checkpoint proposal seen but local re-execution couldn't validate (missing txs, " +
            "timeouts, etc.).",
        countsAsMissed: false,
        note: "Not a terminal verdict — a later poll may upgrade it, so it is excluded from " +
            "missedPct on both sides of the ratio rather than counted as a failure.",
    },
    "checkpoint-missed": {
        case: 2,
        description: "Block proposals seen but no checkpoint proposal.",
        countsAsMissed: true,
    },
    "blocks-missed": {
        case: 1,
        description: "No block proposals seen for the slot.",
        countsAsMissed: true,
        note: "Nothing reached p2p at all, so the elected proposer never acted. Attestation " +
            "counts are zero as a consequence, not as independent evidence about the committee.",
    },
    unknown: {
        case: null,
        description: "Clustec's own addition: the slot fell outside the sentinel's history window, so " +
            "its outcome was never observed.",
        countsAsMissed: false,
        note: "Unrecoverable — sentinel state is evicted from the node's kvStore and L1 carries " +
            "no trace of a non-mined slot. Excluded from missedPct entirely.",
    },
};
/**
 * `orphaned` cannot be a stored `slot_status` value, so it arrives as context rather than as a
 * status. Two independent reasons:
 *
 * The sentinel keeps re-asserting the pre-prune verdict. Its history lives in the node's own
 * kvStore, which an L2 prune does not rewind, so it goes on reporting those slots as
 * `checkpoint-mined`; and `persistRollup` deliberately never downgrades a mined row. On
 * 2026-08-14 the prune handler deleted 62 such rows and a single poll re-inserted all 62 as
 * mined 74ms later, where they have sat ever since.
 *
 * And the fact is a property of the slot's *history*, not of the proposer's conduct in it. The
 * status field answers "what did the proposer do", and the honest answer stays `checkpoint-mined`
 * — it really did mine a checkpoint. What changed is what the chain later did with it.
 */
export function slotOutcome(status, context) {
    if (context?.orphaned)
        return "orphaned";
    if (status === "checkpoint-mined")
        return "filled";
    return SLOT_STATUS_INFO[status]?.countsAsMissed ? "missed" : "indeterminate";
}
export function isMissedStatus(status) {
    return slotOutcome(status) === "missed";
}
/**
 * Whether a slot inside a recorded prune span had its work discarded, as opposed to having
 * failed on its own account.
 *
 * All three clauses are load-bearing, and the reason to be this strict is that a prune span
 * contains slots of both kinds. Taking the 2026-08-14 span (slots 40768–40833) as the worked
 * example:
 *
 *  - `insidePrunedSpan` — the slot is in a span some recorded prune discarded. Necessary, and
 *    nowhere near sufficient.
 *  - `!hasBlock` — nothing survives at that slot today. A slot still holding a block was not
 *    discarded, whatever else happened around it, and this is the clause that keeps a
 *    re-produced slot from being written off.
 *  - `status === "checkpoint-mined"` — the sentinel saw a checkpoint reach L1 there. This is
 *    the clause that separates the two kinds of slot in the span: 40768–40829 were mined and
 *    then discarded, which is orphaned work; 40830–40832 were `checkpoint-valid` (validated on
 *    p2p, never published) and 40833 was `blocks-missed`. Those four failed on their own merits
 *    and stay misses — the prune is not an amnesty for slots that produced nothing.
 *
 * The mainnet prunes of 2026-07-15 reclassify nothing under this rule, because the sentinel
 * reported no mined slot anywhere in either span. That is the rule working, not failing: with no
 * evidence a checkpoint ever reached L1 there, "missed" is what the data supports.
 */
export function isOrphanedSlot(slot) {
    return slot.insidePrunedSpan && !slot.hasBlock && slot.status === "checkpoint-mined";
}
/**
 * The miss split, derived from per-status counts rather than transported alongside them.
 *
 * Which statuses count as a miss is static — it is `SLOT_STATUS_INFO.countsAsMissed`, right
 * above — so an endpoint that shipped a precomputed miss-only array next to the same counts
 * was sending one fact twice and giving the two room to disagree. The percentages are pure
 * arithmetic over the counts, so they belong wherever the counts land.
 *
 * Lives here, in common, for the same reason the taxonomy does: the dashboard card and the
 * health page both draw this split, and two hand-rolled copies of "filter to missed, divide
 * by the missed total, sort" is exactly how the two surfaces drift.
 *
 * Statuses with a zero count are dropped — a legend entry for a status that never occurred is
 * noise. Sorted most frequent first, so the dominant cause reads first.
 */
export function deriveMissCauses(statusCounts) {
    if (statusCounts == null)
        return [];
    const entries = Object.entries(statusCounts);
    const missedTotal = entries.reduce((sum, [status, count]) => (isMissedStatus(status) ? sum + count : sum), 0);
    return entries
        .filter(([status, count]) => count > 0 && isMissedStatus(status))
        .map(([status, count]) => ({
        status,
        count,
        pctOfMissed: missedTotal > 0 ? Math.round((count / missedTotal) * 1000) / 10 : 0,
    }))
        .sort((a, b) => b.count - a.count);
}
/**
 * Missed-slot accounting over a slot list, with the indeterminate and orphaned exclusions
 * applied consistently. `classified` is the denominator for missedPct.
 *
 * Accepts either bare statuses or `{ status, orphaned }` entries, so a caller that has no prune
 * context can pass what it has and get today's numbers unchanged. `orphaned` is reported but kept
 * out of `classified` — see {@link slotOutcome} for why a discarded slot belongs in neither half
 * of the ratio.
 */
export function tallySlotStatuses(slots) {
    let filled = 0;
    let missed = 0;
    let indeterminate = 0;
    let orphaned = 0;
    for (const slot of slots) {
        const outcome = typeof slot === "string"
            ? slotOutcome(slot)
            : slotOutcome(slot.status, { orphaned: slot.orphaned });
        if (outcome === "filled")
            filled++;
        else if (outcome === "missed")
            missed++;
        else if (outcome === "orphaned")
            orphaned++;
        else
            indeterminate++;
    }
    const classified = filled + missed;
    return {
        filled,
        missed,
        indeterminate,
        orphaned,
        classified,
        missedPct: classified > 0 ? (missed / classified) * 100 : 0,
    };
}
//# sourceMappingURL=slot-status.js.map